"use client";
import React from "react";
import { useSettingsController } from "./useSettingsController";
import { PageContainer } from "@/src/components/layout/page-container";
import { Section } from "@/src/components/layout/section";
import { Text } from "@/src/components/information/text";
import { Badge } from "@/src/components/information/badge";
import { Alert } from "@/src/components/information/alert";
import { Button } from "@/src/components/input/button";
import { Input } from "@/src/components/input/input";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/src/components/input/select";
import { Checkbox } from "@/src/components/input/checkbox";
import { FormField } from "@/src/components/input/form-field";
import { Card, CardHeader, CardTitle, CardContent, CardFooter } from "@/src/components/container/card";
import { SectionCard } from "@/src/components/container/section-card";
import { Separator } from "@/src/components/information/separator";
import { ExternalLink } from "lucide-react";

export function SettingsPage() {
  const controller = useSettingsController();

  const renderGeneralSection = () => (
    <Section
      title="General Settings"
      description="Update your company information and contact details"
    >
      <div className="space-y-theme-5">
        <SectionCard>
          <div className="space-y-theme-5">
            <FormField
              label="Company Name"
              htmlFor="companyName"
              required
              hint="Your organization's display name"
            >
              <Input
                id="companyName"
                value={controller.generalSettings.companyName}
                onChange={(e) => controller.updateGeneralSettings({ companyName: e.target.value })}
                aria-required="true"
              />
            </FormField>

            <Separator />

            <div className="grid grid-cols-2 gap-theme-4">
              <FormField
                label="Email"
                htmlFor="email"
                required
              >
                <Input
                  id="email"
                  type="email"
                  value={controller.generalSettings.email}
                  onChange={(e) => controller.updateGeneralSettings({ email: e.target.value })}
                  aria-required="true"
                />
              </FormField>
              <FormField
                label="Phone"
                htmlFor="phone"
              >
                <Input
                  id="phone"
                  value={controller.generalSettings.phone}
                  onChange={(e) => controller.updateGeneralSettings({ phone: e.target.value })}
                />
              </FormField>
            </div>

            <FormField
              label="Website"
              htmlFor="website"
            >
              <Input
                id="website"
                value={controller.generalSettings.website}
                onChange={(e) => controller.updateGeneralSettings({ website: e.target.value })}
              />
            </FormField>

            <FormField
              label="Street Address"
              htmlFor="address"
            >
              <Input
                id="address"
                value={controller.generalSettings.address}
                onChange={(e) => controller.updateGeneralSettings({ address: e.target.value })}
              />
            </FormField>

            <div className="grid grid-cols-2 gap-theme-4">
              <FormField
                label="City"
                htmlFor="city"
              >
                <Input
                  id="city"
                  value={controller.generalSettings.city}
                  onChange={(e) => controller.updateGeneralSettings({ city: e.target.value })}
                />
              </FormField>
              <FormField
                label="State"
                htmlFor="state"
              >
                <Input
                  id="state"
                  value={controller.generalSettings.state}
                  onChange={(e) => controller.updateGeneralSettings({ state: e.target.value })}
                />
              </FormField>
            </div>

            <div className="grid grid-cols-2 gap-theme-4">
              <FormField
                label="Zip Code"
                htmlFor="zipCode"
              >
                <Input
                  id="zipCode"
                  value={controller.generalSettings.zipCode}
                  onChange={(e) => controller.updateGeneralSettings({ zipCode: e.target.value })}
                />
              </FormField>
              <FormField
                label="Country"
                htmlFor="country"
              >
                <Input
                  id="country"
                  value={controller.generalSettings.country}
                  onChange={(e) => controller.updateGeneralSettings({ country: e.target.value })}
                />
              </FormField>
            </div>
          </div>
        </SectionCard>
      </div>
    </Section>
  );

  const renderNotificationsSection = () => (
    <Section
      title="Notification Settings"
      description="Control how and when you receive notifications"
    >
      <div className="space-y-theme-4">
        <SectionCard>
          <div className="flex items-center justify-between">
            <div>
              <Text className="text-sm font-medium">
                Email on Approvals
              </Text>
              <Text className="text-xs text-muted-foreground">
                Receive email when a request is approved
              </Text>
            </div>
            <Checkbox
              checked={controller.notificationSettings.emailOnApprovals}
              onCheckedChange={(checked) => controller.updateNotificationSettings({ emailOnApprovals: checked === true })}
              aria-label="Email on Approvals"
            />
          </div>
        </SectionCard>

        <SectionCard>
          <div className="flex items-center justify-between">
            <div>
              <Text className="text-sm font-medium">
                Email on Rejections
              </Text>
              <Text className="text-xs text-muted-foreground">
                Receive email when a request is rejected
              </Text>
            </div>
            <Checkbox
              checked={controller.notificationSettings.emailOnRejections}
              onCheckedChange={(checked) => controller.updateNotificationSettings({ emailOnRejections: checked === true })}
              aria-label="Email on Rejections"
            />
          </div>
        </SectionCard>

        <SectionCard>
          <div className="flex items-center justify-between">
            <div>
              <Text className="text-sm font-medium">
                Email on Comments
              </Text>
              <Text className="text-xs text-muted-foreground">
                Receive email when someone comments on your request
              </Text>
            </div>
            <Checkbox
              checked={controller.notificationSettings.emailOnComments}
              onCheckedChange={(checked) => controller.updateNotificationSettings({ emailOnComments: checked === true })}
              aria-label="Email on Comments"
            />
          </div>
        </SectionCard>

        <SectionCard>
          <div className="flex items-center justify-between">
            <div>
              <Text className="text-sm font-medium">
                Slack Notifications
              </Text>
              <Text className="text-xs text-muted-foreground">
                Get notified in Slack
              </Text>
            </div>
            <Checkbox
              checked={controller.notificationSettings.slackNotifications}
              onCheckedChange={(checked) => controller.updateNotificationSettings({ slackNotifications: checked === true })}
              aria-label="Slack Notifications"
            />
          </div>
        </SectionCard>

        <SectionCard>
          <div className="flex items-center justify-between">
            <div>
              <Text className="text-sm font-medium">
                Push Notifications
              </Text>
              <Text className="text-xs text-muted-foreground">
                Enable browser push notifications
              </Text>
            </div>
            <Checkbox
              checked={controller.notificationSettings.pushNotifications}
              onCheckedChange={(checked) => controller.updateNotificationSettings({ pushNotifications: checked === true })}
              aria-label="Push Notifications"
            />
          </div>
        </SectionCard>

        <SectionCard>
          <FormField
            label="Email Digest Frequency"
            htmlFor="emailDigest"
          >
            <Select
              value={controller.notificationSettings.emailDigest}
              onValueChange={(value: string) => controller.updateNotificationSettings({ emailDigest: value as "daily" | "weekly" | "never" })}
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="daily">Daily</SelectItem>
                <SelectItem value="weekly">Weekly</SelectItem>
                <SelectItem value="never">Never</SelectItem>
              </SelectContent>
            </Select>
          </FormField>
        </SectionCard>
      </div>
    </Section>
  );

  const renderApprovalsSection = () => (
    <Section
      title="Approval Workflow"
      description="Configure approval rules and thresholds for your organization"
    >
      <div className="space-y-theme-4">
        <SectionCard>
          <div className="flex items-center justify-between">
            <div>
              <Text className="text-sm font-medium">
                Require Multiple Approvals
              </Text>
              <Text className="text-xs text-muted-foreground">
                Require 2+ approvers for all requests
              </Text>
            </div>
            <Checkbox
              checked={controller.approvalSettings.requireMultipleApprovals}
              onCheckedChange={(checked) => controller.updateApprovalSettings({ requireMultipleApprovals: checked === true })}
              aria-label="Require Multiple Approvals"
            />
          </div>
        </SectionCard>

        <SectionCard>
          <div className="flex items-center justify-between">
            <div>
              <Text className="text-sm font-medium">
                Enable Escalation
              </Text>
              <Text className="text-xs text-muted-foreground">
                Automatically escalate if not approved within timeout period
              </Text>
            </div>
            <Checkbox
              checked={controller.approvalSettings.escalationEnabled}
              onCheckedChange={(checked) => controller.updateApprovalSettings({ escalationEnabled: checked === true })}
              aria-label="Enable Escalation"
            />
          </div>
        </SectionCard>

        <SectionCard>
          <FormField
            label="Auto-Approve Requests Under"
            htmlFor="autoApproveUnder"
            hint="Requests below this amount will be automatically approved"
          >
            <div className="relative">
              <span className="absolute left-3 top-3 text-muted-foreground">$</span>
              <Input
                id="autoApproveUnder"
                type="number"
                value={controller.approvalSettings.autoApproveUnder}
                onChange={(e) => controller.updateApprovalSettings({ autoApproveUnder: parseInt(e.target.value) || 0 })}
                className="pl-7"
              />
            </div>
          </FormField>
        </SectionCard>

        <SectionCard>
          <FormField
            label="Approval Timeout (hours)"
            htmlFor="approvalTimeout"
            hint="Time before escalation if not approved"
          >
            <Input
              id="approvalTimeout"
              type="number"
              value={controller.approvalSettings.approvalTimeout}
              onChange={(e) => controller.updateApprovalSettings({ approvalTimeout: parseInt(e.target.value) || 24 })}
            />
          </FormField>
        </SectionCard>

        <SectionCard>
          <FormField
            label="Escalation Level"
            htmlFor="escalationLevel"
          >
            <Select
              value={controller.approvalSettings.escalationLevel}
              onValueChange={(value: string) => controller.updateApprovalSettings({ escalationLevel: value as "department" | "finance" | "executive" })}
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="department">Department Manager</SelectItem>
                <SelectItem value="finance">Finance Team</SelectItem>
                <SelectItem value="executive">Executive</SelectItem>
              </SelectContent>
            </Select>
          </FormField>
        </SectionCard>
      </div>
    </Section>
  );

  const renderIntegrationsSection = () => (
    <Section
      title="Integrations"
      description="Connect external apps and services to BlueCopa"
    >
      <div className="space-y-theme-4">
        {/* Slack Integration */}
        <SectionCard>
          <div className="flex items-start justify-between mb-theme-4">
            <div>
              <Text className="text-h4 mb-theme-1">
                Slack
              </Text>
              <Text className="text-xs text-muted-foreground">
                Get notifications and updates in Slack
              </Text>
            </div>
            {controller.integrationSettings.slackConnected && <Badge variant="success">Connected</Badge>}
          </div>

          {controller.integrationSettings.slackConnected ? (
            <div className="space-y-theme-3">
              <div>
                <Text className="text-xs text-muted-foreground">
                  Workspace
                </Text>
                <Text className="text-sm font-medium">
                  {controller.integrationSettings.slackWorkspace}
                </Text>
              </div>
              <Button variant="outline" size="sm">
                Disconnect
              </Button>
            </div>
          ) : (
            <Button size="sm">
              Connect to Slack
              <ExternalLink className="h-4 w-4 ml-2" />
            </Button>
          )}
        </SectionCard>

        {/* QuickBooks Integration */}
        <SectionCard>
          <div className="flex items-start justify-between mb-theme-4">
            <div>
              <Text className="text-h4 mb-theme-1">
                QuickBooks
              </Text>
              <Text className="text-xs text-muted-foreground">
                Sync invoices and expenses with QuickBooks
              </Text>
            </div>
            {controller.integrationSettings.quickbooksConnected && <Badge variant="success">Connected</Badge>}
          </div>

          {controller.integrationSettings.quickbooksConnected ? (
            <div className="space-y-theme-3">
              <div>
                <Text className="text-xs text-muted-foreground">
                  Realm ID
                </Text>
                <Text className="text-sm font-medium">
                  {controller.integrationSettings.quickbooksRealm}
                </Text>
              </div>
              <Button variant="outline" size="sm">
                Disconnect
              </Button>
            </div>
          ) : (
            <Button size="sm">
              Connect QuickBooks
              <ExternalLink className="h-4 w-4 ml-2" />
            </Button>
          )}
        </SectionCard>

        {/* API & Webhooks */}
        <SectionCard>
          <div className="space-y-theme-4">
            <div>
              <div className="flex items-center justify-between mb-theme-3">
                <Text className="text-h4">
                  API Access
                </Text>
                <Checkbox
                  checked={controller.integrationSettings.apiKeysEnabled}
                  onCheckedChange={(checked) => controller.updateIntegrationSettings({ apiKeysEnabled: checked === true })}
                  aria-label="Enable API Access"
                />
              </div>
              <Text className="text-xs text-muted-foreground">
                Enable API keys for programmatic access
              </Text>
            </div>

            <Separator />

            <div>
              <div className="flex items-center justify-between mb-theme-3">
                <Text className="text-h4">
                  Webhooks
                </Text>
                <Checkbox
                  checked={controller.integrationSettings.webhooksEnabled}
                  onCheckedChange={(checked) => controller.updateIntegrationSettings({ webhooksEnabled: checked === true })}
                  aria-label="Enable Webhooks"
                />
              </div>
              <Text className="text-xs text-muted-foreground">
                Enable webhooks for event notifications
              </Text>
            </div>
          </div>
        </SectionCard>
      </div>
    </Section>
  );

  const renderSectionContent = () => {
    switch (controller.activeSection) {
      case "general":
        return renderGeneralSection();
      case "notifications":
        return renderNotificationsSection();
      case "approvals":
        return renderApprovalsSection();
      case "integrations":
        return renderIntegrationsSection();
      default:
        return null;
    }
  };

  const sectionDirtyCount = Array.from(controller.dirtyFields).filter((field) =>
    field.startsWith(`${controller.activeSection}.`)
  ).length;

  return (
    <PageContainer className="!px-0 !py-0">
      <div className="space-y-theme-6">
        {/* Header */}
        <div className="animate-fade-in-up">
          <Text className="text-h2 font-bold mb-theme-2">
            Settings
          </Text>
          <Text className="text-sm text-muted-foreground">
            Manage your preferences and integrations
          </Text>
        </div>

        {/* Alert Messages */}
        {controller.error && (
          <Alert
            variant="destructive"
            title="Error"
            description={controller.error}
          />
        )}

        {controller.success && (
          <Alert
            variant="success"
            title="Success"
            description={controller.success}
          />
        )}

        {/* Main Content */}
        <div className="grid grid-cols-4 gap-theme-6 animate-fade-in-up delay-2">
          {/* Left Navigation Sidebar */}
          <div>
            <nav className="space-y-theme-2 sticky top-8">
              {controller.sections.map((section) => (
                <button
                  key={section.id}
                  onClick={() => controller.setActiveSection(section.id)}
                  className={`w-full text-left px-theme-4 py-theme-3 rounded-lg transition-all duration-200 relative font-medium text-sm ${
                    controller.activeSection === section.id
                      ? "bg-primary/10 text-primary border border-primary/30 shadow-sm"
                      : "text-foreground hover:bg-accent/40 border border-transparent hover:border-border"
                  }`}
                >
                  <div className="flex items-center justify-between">
                    <span>{section.label}</span>
                    {controller.activeSection === section.id && sectionDirtyCount > 0 && (
                      <Badge variant="warning" size="sm">
                        {sectionDirtyCount}
                      </Badge>
                    )}
                  </div>
                </button>
              ))}
            </nav>
          </div>

          {/* Right Content Area */}
          <div className="col-span-3">
            <div className="space-y-theme-5">
              {/* Section Content */}
              {renderSectionContent()}

              {/* Separator before Action Buttons */}
              <Separator />

              {/* Action Buttons */}
              <div className="flex gap-theme-3 justify-end">
                <Button
                  variant="outline"
                  onClick={() => controller.resetSection(controller.activeSection)}
                  disabled={sectionDirtyCount === 0 || controller.saving}
                >
                  Discard Changes
                </Button>
                <Button
                  onClick={() => controller.saveSection(controller.activeSection)}
                  disabled={sectionDirtyCount === 0 || controller.saving}
                >
                  {controller.saving ? "Saving..." : "Save Changes"}
                </Button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </PageContainer>
  );
}
